import { useCallback, useEffect, useState, useRef } from "react";
import { Bell, X, Volume2, Loader2 } from "lucide-react";
import { listNotifications, readNotification, readAllNotifications, fetchTtsBlob } from "@/lib/api";

/** Notification bell + panel. Polls every 15s. Voice only when the owner opted in. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [speaking, setSpeaking] = useState(null);
  const ref = useRef(null);
  const audioRef = useRef(null);

  const queueRef = useRef([]);
  const speak = useCallback(async function speakNotification(n) {
    if (!n?.voice_text && !n?.title) return;
    if (window.__nadiaSpeaking || (audioRef.current && !audioRef.current.ended && !audioRef.current.paused)) {
      if (!queueRef.current.find((q) => q.id === n.id)) queueRef.current.push(n); // never interrupt a reading in progress
      return;
    }
    try {
      setSpeaking(n.id);
      window.__nadiaSpeaking = true;
      const blob = await fetchTtsBlob(n.voice_text || n.title);
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      const next = () => { setSpeaking(null); audioRef.current = null; window.__nadiaSpeaking = false; const q = queueRef.current.shift(); if (q) speakNotification(q); };
      audio.onended = next;
      audio.onerror = next;
      await audio.play();
    } catch {
      setSpeaking(null);
      audioRef.current = null;
      window.__nadiaSpeaking = false;
    }
  }, []);

  const load = useCallback(() => {
    listNotifications()
      .then((d) => {
        setItems(d.items);
        setUnread(d.unread_count);
        const first = d.items && d.items[0];
        if (first && !first.read) {
          const push = first.channels?.some((c) => c.type === "browser_push");
          if (push && "Notification" in window && Notification.permission === "granted") {
            const key = `notif-${first.id}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, "1");
              new Notification(first.title, { body: first.subtitle });
            }
          }
          // Read aloud only if the owner enabled voice in Impostazioni
          const voice = first.channels?.some((c) => c.type === "voice");
          if (voice) {
            const vkey = `voice-${first.id}`;
            if (!sessionStorage.getItem(vkey)) {
              sessionStorage.setItem(vkey, "1");
              speak(first);
            }
          }
        }
      })
      .catch(() => {});
  }, [speak]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const h = (e) => {
      if (!open) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const openOne = async (n) => {
    if (!n.read) {
      await readNotification(n.id);
      load();
    }
  };
  const markAll = async () => {
    await readAllNotifications();
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-cyan-200 hover:bg-white/[0.04] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute top-1 right-1 min-w-[16px] h-[16px] rounded-full bg-cyan-400 text-slate-950 text-[10px] font-bold flex items-center justify-center px-1"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-w-[90vw] rounded-2xl border border-white/10 bg-[#0F172A]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-40" data-testid="notification-panel">
          <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
            <div className="text-sm font-heading font-semibold text-slate-100">Notifiche</div>
            <div className="flex gap-2">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-[11px] text-cyan-300 hover:text-cyan-200"
                  data-testid="mark-all-read"
                >
                  Segna lette
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-200">
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Nessuna notifica ancora.</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openOne(n)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer ${
                    n.read ? "opacity-60" : ""
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-100 truncate">{n.title}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5 truncate">{n.subtitle}</div>
                      <div className="mt-1.5 flex gap-1 flex-wrap">
                        {(n.channels || []).map((c, i) => (
                          <span
                            key={i}
                            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/10 text-slate-400"
                          >
                            {c.type.replace("_", " ")}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className="text-[10px] text-slate-500">{fmt(n.created_at)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); speak(n); }}
                        title="Ascolta"
                        className={`w-7 h-7 rounded-md flex items-center justify-center border border-white/10 transition-colors ${
                          speaking === n.id ? "text-cyan-200 bg-cyan-500/10 border-cyan-400/40" : "text-slate-400 hover:text-cyan-200 hover:border-cyan-400/40"
                        }`}
                        data-testid={`notification-listen-${n.id}`}
                      >
                        {speaking === n.id ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function fmt(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 60000;
    if (diff < 1) return "adesso";
    if (diff < 60) return `${Math.round(diff)} min`;
    if (diff < 60 * 24) return `${Math.round(diff / 60)} h`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}
